import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { OLD_API_ENVELOPES } from '@/lib/envelopeTag';
import { errorCode, errorMessage } from '@/lib/errors';
import { newOperationId, useReverseFinanceMovement } from '@/lib/finance';
import { formatMinor } from '@/lib/money';
import { MovementForm } from './MovementForm';
import type {
  FinanceAccount,
  FinanceCategory,
  FinanceCounterparty,
  FinanceMovement,
} from '@/api/generated/types.gen';

export interface ReverseDialogProps {
  movement: FinanceMovement;
  accounts: readonly FinanceAccount[];
  categories: readonly FinanceCategory[];
  counterparties?: readonly FinanceCounterparty[];
  apartments?: readonly { id: string; title: string }[];
  businessTz?: string;
  /**
   * The operator holds finance.manage. Reversing a payment that put money
   * INTO an envelope takes it out again, which needs it (envelopes spec §6);
   * so does a replacement tagged «into the envelope».
   */
  canManage: boolean;
  onClose: () => void;
}

/**
 * Cancel a payment, optionally recording the one that should have been there
 * instead (§8.6). Both halves go under ONE operation_id: the reversal and its
 * replacement are one operation, and the server overwrites the replacement's
 * own key with it.
 *
 * A payment in a closed month is mirrored into business today with
 * correction_for_month set — the server does that, not this dialog, so
 * nothing here has to know which months are closed.
 */
export function ReverseDialog({
  movement,
  accounts,
  categories,
  counterparties,
  apartments,
  businessTz,
  canManage,
  onClose,
}: ReverseDialogProps) {
  const [reason, setReason] = useState('');
  const [replace, setReplace] = useState(false);
  const [operationId] = useState(newOperationId);
  const [failure, setFailure] = useState<string | null>(null);
  // The server's refusal of the whole plan over the replacement's tag:
  // MovementForm shows it at the envelope field, or in its general alert
  // while that field is hidden.
  const [envelopeFailure, setEnvelopeFailure] = useState<string | null>(null);
  const mut = useReverseFinanceMovement();

  const reasonReady = reason.trim().length > 0;
  // No envelope_moves on the answer = an older API, not «no tag». The
  // replacement would have to restate an envelope side it cannot see.
  const oldApi = !Array.isArray(movement.envelope_moves);
  // A reversal mirrors every envelope move the payment wrote itself (not one
  // that mirrors another); an `in` among them becomes an `out`, which needs
  // finance.manage (envelopes spec §6). Judged by the moves' own directions,
  // not the header's: a payment from before OH-195 may carry both.
  const needsManage = (movement.envelope_moves ?? []).some((m) => !m.reverses_id && m.direction === 'in');
  const blocked = needsManage && !canManage;

  function run(replacement?: Parameters<NonNullable<Parameters<typeof MovementForm>[0]['onCompose']>>[0]) {
    setFailure(null);
    setEnvelopeFailure(null);
    mut.mutate(
      {
        id: movement.id,
        body: {
          operation_id: operationId,
          reason: reason.trim(),
          ...(replacement ? { replacement } : {}),
        },
      },
      {
        onSuccess: onClose,
        onError: (e) => {
          // A refusal of a body that carried a tag is the envelope field's.
          // Without a tag (e.g. «No envelope tag» on a payment with several
          // moves) the refusal is about the reversal's own mirror and goes
          // to the general alert — never nowhere.
          if (replacement?.envelope && errorCode(e) === 'envelope_insufficient') setEnvelopeFailure(errorMessage(e));
          else setFailure(errorMessage(e));
        },
      },
    );
  }

  return (
    <Dialog
      open
      side
      onClose={onClose}
      title="Reverse this payment"
      description={`${formatMinor(movement.amount_minor, movement.currency)} on ${movement.occurred_on}`}
      footer={
        // With a replacement the inner form owns the submit — one click, one
        // operation. Two buttons would invite a reversal with a half-filled
        // replacement.
        replace ? (
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!reasonReady || blocked}
              loading={mut.isPending}
              onClick={() => run()}
            >
              Reverse
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Reason"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            // Emptying the reason takes the replacement flag with it, or the
            // dialog becomes a dead end (no Reverse button, no form, no
            // explanation).
            if (!e.target.value.trim()) setReplace(false);
          }}
          hint="Kept on the mirror — it is the audit trail for the correction"
          required
          autoFocus
        />
        <Checkbox
          label="Also record the payment that should have been there"
          description="Written under the same operation, so the pair cannot half-apply"
          checked={replace}
          onChange={(e) => setReplace(e.target.checked)}
          disabled={!reasonReady || blocked || oldApi}
        />
        {oldApi && <p className="text-xs text-[var(--ollio-gold-deep)]">{OLD_API_ENVELOPES}</p>}
        {blocked && (
          <p className="text-sm text-[var(--ollio-gold-deep)]">
            This payment put money into an envelope; reversing it takes that money out again and
            requires finance.manage.
          </p>
        )}
        {/* Nothing below reads `replace` while the reason is empty — see the
            Input's onChange, which drops it. Without that, clearing the
            reason left the flag set: the replacement form hid itself (it
            needs a reason) and the footer showed Cancel alone, so the dialog
            could neither reverse nor explain why. */}
        {!reasonReady && (
          <p className="text-xs text-muted">The reason comes first — a correction without one is not one.</p>
        )}
        {replace && reasonReady && (
          <div className="border-t border-[var(--border-hairline)] pt-4">
            <MovementForm
              accounts={accounts}
              categories={categories}
              counterparties={counterparties}
              apartments={apartments}
              businessTz={businessTz}
              // The replacement starts as a copy of what it replaces (§8.6).
              // An operator opening this form came to change ONE thing —
              // usually the article; a blank form defaulted the account to
              // accounts[0], so fixing the article moved the money to another
              // account and nothing on screen said so.
              initialFrom={movement}
              // …including the settle line(s) of a payment that settled an
              // obligation: the server takes their targets off the original
              // and pairs them by position, so the form has to repeat them
              // (§8.6 R15). Without this the form dropped them, the operator
              // saw a blank expense line, and the reversal came back 422 —
              // which made a settlement unreplaceable from the interface.
              replacement
              submitLabel="Reverse and replace"
              busy={mut.isPending}
              canManage={canManage}
              envelopeFailure={envelopeFailure}
              onEnvelopeEdit={() => setEnvelopeFailure(null)}
              onCompose={(body) => run(body)}
            />
          </div>
        )}
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
